import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cdnUrl } from "commons/cdn";
import {
  ChevronDownIcon,
  ClipboardIcon,
  Component1Icon,
  DownloadIcon,
  HeartFilledIcon,
  IdCardIcon,
  LayersIcon,
  StarFilledIcon,
} from "@radix-ui/react-icons";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEditorStore } from "CanvasCore/uiSlice";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { optimisticUpdateAvatars, useUpdateAvatars } from "api";
import {
  delFetcher,
  fetcher,
  getFetcher,
  postFetcher,
  putFetcher,
} from "api/swr";
import useSWR, { mutate } from "swr";
import { useContetLang } from "hooks/config";
import useSWRMutation from "swr/mutation";
import DisplayUpdateTime from "ui/DisplayUpdateTime";
import { useCardAtom } from "./DisplayAvatars";
import { toast } from "@/components/ui/use-toast";
import { usePermissions } from "hooks/useProCptPermission";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { TagPickerDialog } from "components/template-tags";
import { MyImage } from "components/MyImage";
import { MyMedia } from "components/MyMedia";
import { ContentLangs, ProductOptions } from "config";
import { resolveAvatarOutfit } from "commons/avatarOutfit";
import { applyStaticOutfitToEditor } from "Editor/lib/applyStaticOutfit";

const formatContentLang = (code) => {
  const lang = ContentLangs.find((item) => item.code === Number(code));
  if (!lang) return String(code);
  return `${lang.short} (${code})`;
};

const formatProductId = (code) => {
  const product = ProductOptions.find((item) => item.code === Number(code));
  if (!product) return String(code);
  return `${product.name} (${code})`;
};

const clickableTextClassName =
  "cursor-pointer font-medium underline underline-offset-4";

const StateLabel = ({ state }) => {
  const label = state === 401 ? "public" : "local";
  return (
    <span className="w-fit rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
      {label}
    </span>
  );
};

const AvatarItem = ({ avatar, onAuthorClick = undefined, onAidClick = undefined }) => {
  const showInfo = useCardAtom((state) => state.info);
  const showBadge = useCardAtom((state) => state.badge);
  const showHeader = useCardAtom((state) => state.header);
  const showReportCount = useCardAtom((state) => state.showReportCount);

  if (!avatar) {
    return null;
  }

  return (
    <div className="flex flex-col gap-1 border p-1 min-h-0 h-full">
      <div className="flex-1 flex flex-col gap-1">
        {showHeader && (
          <div className="text-sm">
            <h1 className="">
              <HeaderText data={avatar} />
            </h1>
            <p className="text-xs text-muted-foreground">
              <DisplayUpdateTime updateTime={avatar.updateTime} />
            </p>
          </div>
        )}

        <div className="relative">
          <div className="absolute left-1 top-1 z-10">
            <StateLabel state={avatar.state} />
          </div>
          {avatar?.engineWith === "spine" &&
          (avatar?.videoUrl || avatar?.videoPreviewUrl) ? (
            <MyMedia
              alt="avatar"
              videoUrl={avatar.videoPreviewUrl || avatar.videoUrl}
              previewVideoUrl={avatar.videoUrl || avatar.videoPreviewUrl}
              imageUrl={avatar.url ?? avatar.thumb}
              className="aspect-square w-full rounded bg-black transition-all"
            />
          ) : (
            <MyImage
              alt="avatar"
              src={cdnUrl(avatar?.thumb ?? avatar.url)}
              previewSrc={cdnUrl(avatar?.url ?? avatar.thumb)}
              className="aspect-square w-full rounded bg-black object-contain transition-all"
            />
          )}
        </div>

        {showInfo && (
          <div className="flex flex-col space-y-1">
            {onAuthorClick && avatar?.authorId ? (
              <p
                className={clickableTextClassName}
                onClick={() => onAuthorClick(avatar.authorId)}
              >
                @{avatar.authorName}
              </p>
            ) : (
              <p>@{avatar.authorName}</p>
            )}
            <div className="flex items-center gap-2">
              <LayersIcon />
              {avatar?.layerCount}
              <Component1Icon />
              {avatar?.cptCount}
            </div>
            <UpdateLike avatar={avatar} />

            <div className="flex items-center gap-2">
              <StarFilledIcon className="text-destructive" />
              {avatar.starCount ?? 0}
            </div>

            <div className="flex items-center gap-2">
              <DownloadIcon />
              {avatar.dailyCount?.downloadCount ?? 0}/{avatar.dCount ?? 0}
            </div>
            <div className="flex items-center gap-2">
              Recreate:
              {onAidClick && avatar?.id ? (
                <span
                  className={clickableTextClassName}
                  onClick={() => onAidClick(avatar.id)}
                >
                  {avatar.dailyCount?.reCreateCount}/{avatar.reCreateCount}
                </span>
              ) : (
                <span>
                  {avatar.dailyCount?.reCreateCount}/{avatar.reCreateCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              Remix:
              {onAidClick && avatar?.id ? (
                <span
                  className={clickableTextClassName}
                  onClick={() => onAidClick(avatar.id)}
                >
                  {avatar.subAvatarCount ?? 0}
                </span>
              ) : (
                <span>{avatar.subAvatarCount ?? 0}</span>
              )}
            </div>

            <OcInfo avatar={avatar} />
          </div>
        )}
        {showReportCount && (
          <div className="flex items-center gap-2">
            Reported:
            <span>{avatar.reportCount ?? 0}</span>
          </div>
        )}
        {showBadge && (
          <div className="flex flex-wrap gap-2 items-center">
            {/* Remix列表 Simple === 1 */}
            {avatar?.simple === 1 && <Badge>Remix</Badge>}
            {/* Sub列表 从Avatar2创 有Aid */}
            {avatar?.aid && <Badge>Sub</Badge>}
            {/* OC列表 有desc */}
            {avatar?.desc && <Badge>OC</Badge>}
            {avatar?.tag && <Badge>{avatar?.tag}</Badge>}
            {/* <RenderTags avatar={avatar} /> */}
          </div>
        )}
      </div>
      <div className="mt-auto w-full">
        <Actions avatar={avatar} />
      </div>
    </div>
  );
};
const OcInfo = ({ avatar }) => {
  if (!avatar.desc) return null;
  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <div className="flex items-center gap-2">
          <IdCardIcon />
          OC
        </div>
      </HoverCardTrigger>
      <HoverCardContent className="w-80">
        <div className="flex flex-col justify-between gap-4">
          {avatar.desc}
          <pre>
            {JSON.stringify(JSON.parse(avatar.avatarInfo ?? "{}"), null, 1)}
          </pre>
        </div>
      </HoverCardContent>
    </HoverCard>
  );
};

const ActionSetDefaultCarousel = ({ avatar }) => {
  const { trigger, isMutating } = useSWRMutation(
    "/avatars/" + avatar.id,
    putFetcher,
  );

  const updateDefaultCarousel = (e) => {
    e.preventDefault();
    if (isMutating) return;

    // 如果已经设置为默认轮播图，则取消设置；否则设置为默认轮播图
    const newValue = avatar.defaultCarousel === 1 ? 0 : 1;
    const body = { key: "defaultCarousel", value: newValue };
    const oldValue = avatar.defaultCarousel;

    // 乐观更新本地数据
    mutate(
      (key) => Array.isArray(key) && key[0] === "/avatars",
      (data) =>
        data.map((item) =>
          item.id === avatar.id ? { ...item, defaultCarousel: newValue } : item,
        ),
      { revalidate: false },
    );

    trigger(body, {
      onSuccess: () => {
        toast({
          title: "Success",
          description:
            newValue === 1
              ? "Set as default carousel image"
              : "Removed from default carousel",
          className: "bg-green-500 text-white",
        });
      },
      onError: (error) => {
        // 回滚乐观更新
        mutate(
          (key) => Array.isArray(key) && key[0] === "/avatars",
          (data) =>
            data.map((item) =>
              item.id === avatar.id
                ? { ...item, defaultCarousel: oldValue }
                : item,
            ),
          { revalidate: false },
        );
        toast({
          variant: "destructive",
          title: "Failed",
          description: error.message || "Update failed",
        });
      },
    });
  };

  // 只在 clientVer === 0 时显示（兼容数字0和字符串'0'）
  if (avatar.clientVer !== "0" && avatar.clientVer !== 0) {
    return null;
  }

  const isSet = avatar.defaultCarousel === 1;

  return (
    <DropdownMenuItem
      onClick={updateDefaultCarousel}
      className="text-destructive"
    >
      {isMutating
        ? "updating..."
        : isSet
          ? "取消设置为模版默认轮播图"
          : "设置为模版默认轮播图"}
    </DropdownMenuItem>
  );
};

const ActionSetOffline = ({ avatar }) => {
  //TODO: miss undo offline
  //TODO: miss onSuccess
  const { trigger, isMutating } = useSWRMutation(
    "/avatars/" + avatar.id,
    putFetcher,
    {
      onSuccess: () => {},
    },
  );

  const updateState = (e) => {
    e.preventDefault();
    if (isMutating) return;
    const value = avatar.state === 401 ? 0 : 401;
    const body = { key: "state", value };
    // 乐观更新
    mutate(
      (key) => Array.isArray(key) && key[0] === "/avatars",
      (data) =>
        data.map((item) =>
          item.id === avatar.id ? { ...item, state: value } : item,
        ),
      { revalidate: false },
    );
    trigger(body, {
      onError: () => {
        // 回滚：简单做一次刷新，确保数据一致
        mutate((key) => Array.isArray(key) && key[0] === "/avatars");
      },
    });
  };

  if (avatar.state !== 401) {
    return (
      <DropdownMenuItem onClick={updateState} className="text-destructive">
        Set to online
      </DropdownMenuItem>
    );
  }
  return (
    <DropdownMenuItem onClick={updateState} className="text-destructive">
      Set to offline
    </DropdownMenuItem>
  );
};

const ActionSetCover = ({ avatar }) => {
  const [open, setOpen] = useState(false);
  const { trigger, isMutating } = useSWRMutation(
    `/templates/${avatar?.tid}`,
    putFetcher,
  );

  const handleOpen = (e) => {
    e.preventDefault();
    setOpen(true);
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    if (isMutating) return false;
    const isSpine = avatar?.engineWith === "spine";
    const value = {
      cover: avatar.url,
      initDataId: avatar.id,
      videoCover: isSpine ? avatar.videoUrl || "" : "",
      videoCoverPreview: isSpine ? avatar.videoPreviewUrl || "" : "",
    };
    trigger(
      { key: "cover", value: JSON.stringify(value) },
      {
        onSuccess: () => {
          toast({
            title: "Set cover submitted",
            className: "bg-green-500 text-white",
          });
          setOpen(false);
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            title: "Set cover failed",
            description: error?.message || "Please try again",
          });
        },
      },
    );
  };

  return (
    <>
      <DropdownMenuItem onClick={handleOpen}>
        {isMutating ? "updating..." : "set cover"}
      </DropdownMenuItem>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set this avatar as template cover?</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isMutating}>
              {isMutating ? "Updating..." : "Confirm"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ActionSetPreview = ({ avatar }) => {
  const [open, setOpen] = useState(false);
  const { trigger, isMutating } = useSWRMutation(
    `/templates/${avatar?.tid}`,
    putFetcher,
  );

  const handleOpen = (e) => {
    e.preventDefault();
    setOpen(true);
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    if (isMutating) return false;
    const isSpine = avatar?.engineWith === "spine";
    const value = {
      preview: avatar.url,
      previewId: avatar.id || "",
      videoPreview: isSpine ? avatar.videoUrl || "" : "",
      videoPreviewPreview: isSpine ? avatar.videoPreviewUrl || "" : "",
    };
    trigger(
      { key: "preview", value: JSON.stringify(value) },
      {
        onSuccess: () => {
          toast({
            title: "Set preview submitted",
            className: "bg-green-500 text-white",
          });
          setOpen(false);
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            title: "Set preview failed",
            description: error?.message || "Please try again",
          });
        },
      },
    );
  };

  return (
    <>
      <DropdownMenuItem onClick={handleOpen}>
        {isMutating ? "updating..." : "set preview"}
      </DropdownMenuItem>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set this avatar as template preview?</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isMutating}>
              {isMutating ? "Updating..." : "Confirm"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const AvatarGotoEditor = ({ avatar }) => {
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  return (
    <DropdownMenuItem
      disabled={pending}
      onClick={async (e) => {
        e.preventDefault();
        if (pending) return;
        setPending(true);
        try {
          const outfit = await resolveAvatarOutfit(avatar);
          const tid = outfit.tid || avatar.tid;
          navigate("/editor/" + tid);
          await applyStaticOutfitToEditor(tid, outfit);
        } catch (err) {
          toast({
            variant: "destructive",
            title: "Failed",
            description:
              err instanceof Error ? err.message : "Missing drawData",
          });
        } finally {
          setPending(false);
        }
      }}
    >
      {pending ? "Loading…" : "Go to editor"}
    </DropdownMenuItem>
  );
};

const AvatarGotoView = ({ avatar }) => {
  const { setDrawers, setTonings } = useEditorStore((state) => state);
  return (
    <DropdownMenuItem
      onClick={(e) => {
        e.preventDefault();
        window.open(
          `/review/avatar/${avatar.tid}?avatarId=${avatar.id}`,
          "_blank",
        );
        setDrawers(avatar.tid, []);
        setTonings(avatar.tid, {});
      }}
    >
      Go to view
    </DropdownMenuItem>
  );
};

const AvatarActionCopyId = ({ avatar }) => {
  return (
    <DropdownMenuItem onSelect={() => navigator.clipboard.writeText(avatar.id)}>
      <ClipboardIcon className="w-4 h-4 mr-2" />
      Copy ID
    </DropdownMenuItem>
  );
};

const AvatarActionCopyShortId = ({ avatar }) => {
  return (
    <DropdownMenuItem
      onSelect={() => navigator.clipboard.writeText(avatar.shortId)}
    >
      <ClipboardIcon className="w-4 h-4 mr-2" />
      Copy Short ID
    </DropdownMenuItem>
  );
};

const AvatarActionCopyTid = ({ avatar }) => {
  return (
    <DropdownMenuItem
      onSelect={() => navigator.clipboard.writeText(avatar.tid || "")}
    >
      <ClipboardIcon className="w-4 h-4 mr-2" />
      Copy TID
    </DropdownMenuItem>
  );
};

const AvatarActionCopyImageUrl = ({ avatar }) => {
  return (
    <DropdownMenuItem
      onSelect={() => navigator.clipboard.writeText(avatar.url)}
    >
      <ClipboardIcon className="w-4 h-4 mr-2" /> Copy Image Url
    </DropdownMenuItem>
  );
};

const AvatarActionCopyImageThumbUrl = ({ avatar }) => {
  return (
    <DropdownMenuItem
      onSelect={() => navigator.clipboard.writeText(avatar.thumb)}
    >
      <ClipboardIcon className="w-4 h-4 mr-2" /> Copy Thumb Url
    </DropdownMenuItem>
  );
};

const ActionSetSimple = ({ avatar }) => {
  const { trigger, isMutating } = useSWRMutation(
    `/avatars/${avatar.id}`,
    putFetcher,
  );

  const updateSimple = (e) => {
    e.preventDefault();
    if (isMutating) {
      return false;
    }
    const newValue = avatar?.simple ? 0 : 1;
    const body = { key: "simple", value: newValue };

    // 立即乐观更新本地数据，不触发重新验证
    mutate(
      (key) => {
        return Array.isArray(key) && key[0] === "/avatars";
      },
      (data) => {
        return data.map((item) =>
          item.id === avatar.id ? { ...item, simple: newValue } : item,
        );
      },
      { revalidate: false }, // 关键：设置为 false 避免重新验证
    );

    trigger(body, {
      onSuccess: () => {
        toast({
          title: "Success",
          description:
            newValue === 1 ? "Marked as Remix" : "Removed Remix mark",
          className: "bg-green-500 text-white",
        });
      },
      onError: (error) => {
        // 如果失败，回滚乐观更新
        mutate(
          (key) => {
            return Array.isArray(key) && key[0] === "/avatars";
          },
          (data) => {
            return data.map((item) =>
              item.id === avatar.id ? { ...item, simple: avatar.simple } : item,
            );
          },
          { revalidate: false },
        );
        toast({
          variant: "destructive",
          title: "Failed",
          description: error.message || "Update failed",
        });
      },
    });
  };
  return (
    <DropdownMenuItem onSelect={updateSimple}>
      {isMutating
        ? "updating..."
        : avatar?.simple === 1
          ? "undo Remix"
          : "Mark as Remix"}
    </DropdownMenuItem>
  );
};

const ActionSetDlcCover = ({ avatar }) => {
  const [pendingDlc, setPendingDlc] = useState(null);
  const { data: dlcs, isLoading } = useSWR(
    [avatar?.tid ? "/dlcs" : null, { tid: avatar.tid }],
    fetcher,
  );

  const { trigger, isMutating } = useSWRMutation(
    pendingDlc ? `/dlcs/${pendingDlc.id}` : null,
    putFetcher,
  );

  const handleOpen = (e, dlc) => {
    e.preventDefault();
    setPendingDlc(dlc);
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    if (isMutating || !pendingDlc) return false;
    trigger(
      { tid: avatar.tid, key: "cover", value: avatar.url },
      {
        onSuccess: () => {
          toast({
            title: "Set dlc icon submitted",
            className: "bg-green-500 text-white",
          });
          setPendingDlc(null);
        },
        onError: (error) => {
          toast({
            variant: "destructive",
            title: "Set dlc icon failed",
            description: error?.message || "Please try again",
          });
        },
      },
    );
  };

  if (isLoading) {
    return (
      <DropdownMenuItem onClick={(e) => e.preventDefault()}>
        fetch dlcs...
      </DropdownMenuItem>
    );
  }

  if (!dlcs || dlcs?.length === 0) {
    return (
      <DropdownMenuItem onClick={(e) => e.preventDefault()}>
        no dlcs
      </DropdownMenuItem>
    );
  }

  return (
    <>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>set as dlc icon</DropdownMenuSubTrigger>
        <DropdownMenuPortal>
          <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
            {dlcs?.map((dlc) => {
              return (
                <DropdownMenuItem
                  key={dlc.id}
                  onClick={(e) => handleOpen(e, dlc)}
                >
                  {dlc.cover && (
                    <img
                      src={cdnUrl(dlc.cover)}
                      alt="..."
                      className="w-16 h-16 mr-2"
                    />
                  )}
                  {dlc.name}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuSubContent>
        </DropdownMenuPortal>
      </DropdownMenuSub>
      <Dialog
        open={Boolean(pendingDlc)}
        onOpenChange={(next) => {
          if (!next) setPendingDlc(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Set this avatar as dlc icon
              {pendingDlc?.name ? ` for "${pendingDlc.name}"` : ""}?
            </DialogTitle>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setPendingDlc(null)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirm} disabled={isMutating}>
              {isMutating ? "Updating..." : "Confirm"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const AvatarActionDelete = ({ avatar }) => {
  const { trigger, isMutating } = useSWRMutation("/avatars", delFetcher);
  const [open, setOpen] = useState(false);

  const handleOpen = (e) => {
    e.preventDefault();
    setOpen(true);
  };

  const handleConfirm = (e) => {
    e.preventDefault();
    if (isMutating) return false;
    trigger(
      { id: avatar.id },
      {
        onSuccess: () => {
          mutate(
            (key) => Array.isArray(key) && key[0] === "/avatars",
            (data) => data?.filter((item) => item.id !== avatar.id),
            { revalidate: false },
          );
          toast({
            title: "删除任务已提交",
            className: "bg-green-500 text-white",
          });
          setOpen(false);
        },
        onError: (error) => {
          const msg = error?.message || "";
          toast({
            variant: "destructive",
            title: "删除任务提交失败",
            description: msg.includes("no permission")
              ? "你没有 Avatar 删除权限，请联系管理员开通"
              : msg || "请稍后重试",
          });
        },
      },
    );
  };

  return (
    <>
      <DropdownMenuItem onClick={handleOpen} className="text-destructive">
        Delete it
      </DropdownMenuItem>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Are you sure to delete this avatar?</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setOpen(false)}
              disabled={isMutating}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirm}
              disabled={isMutating}
            >
              {isMutating ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const ActionSetTag = ({ avatar }) => {
  const [open, setOpen] = useState(false);
  const { handleUpdate, isMutating } = useUpdateAvatars(avatar.id);
  const initialTags = avatar?.tag ? [avatar.tag] : [];

  const handleConfirm = async (selected) => {
    const nextTag = selected[0] || "";
    if ((avatar?.tag || "") === nextTag) return;
    await handleUpdate("addTag", nextTag);
  };

  return (
    <TagPickerDialog
      open={open}
      onOpenChange={setOpen}
      title={`Set Tag${avatar?.tag ? ` (${avatar.tag})` : ""}`}
      initialTags={initialTags}
      selectionMode="single"
      onConfirm={handleConfirm}
      busy={isMutating}
      trigger={
        <DialogTrigger asChild>
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            set tag{avatar?.tag ? `(${avatar.tag})` : ""}
          </DropdownMenuItem>
        </DialogTrigger>
      }
    />
  );
};

const UpdateLike = ({ avatar }) => {
  const { trigger } = useSWRMutation(`/avatars/${avatar.id}/like`, postFetcher);

  const handleDone = () => {
    mutate(
      (key) => Array.isArray(key) && key[0] === "/avatars",
      (data) =>
        data?.map((item) =>
          item.id === avatar.id
            ? { ...item, likeCount: (item.likeCount || 0) + 5 }
            : item,
        ),
      { revalidate: false },
    );
    trigger({ increment: 5 });
  };

  return (
    <div className="flex items-center gap-2">
      <HeartFilledIcon className="text-destructive" />
      {avatar.likeCount}
      <Badge
        variant="outline"
        className="cursor-pointer hover:bg-primary"
        onClick={handleDone}
      >
        +5
      </Badge>
    </div>
  );
};

const HeaderText = ({ data }) => {
  const { clientVer, contentLang } = data;
  const { code, short } = useContetLang(contentLang);
  return (
    <>
      {code} - {short} - {clientVer}
    </>
  );
};

export default AvatarItem;

const ActionCheckFrameStatus = ({ avatar }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [frame, setFrame] = useState(null);
  const [error, setError] = useState(null);

  const handleOpen = async (e) => {
    e.preventDefault();
    if (!avatar?.tid) {
      toast({
        variant: "destructive",
        title: "缺少 tid",
        description: "该 avatar 没有关联模版",
      });
      return;
    }

    setOpen(true);
    setLoading(true);
    setError(null);
    setFrame(null);
    try {
      const data = await getFetcher(
        `/frames/${encodeURIComponent(avatar.tid)}`,
      );
      if (!data || typeof data !== "object") {
        setError("未找到对应 frame");
        return;
      }
      setFrame(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "查询失败");
    } finally {
      setLoading(false);
    }
  };

  const contentLangs = Array.isArray(frame?.contentLangs)
    ? frame.contentLangs
    : [];
  const productIds = Array.isArray(frame?.productIds) ? frame.productIds : [];

  return (
    <>
      <DropdownMenuItem onClick={handleOpen}>
        check frame status
      </DropdownMenuItem>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Frame Status</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div>
              <span className="text-muted-foreground">TID: </span>
              <span className="font-medium">{avatar.tid}</span>
            </div>
            {loading ? (
              <p className="text-muted-foreground">加载中…</p>
            ) : error ? (
              <p className="text-destructive">{error}</p>
            ) : (
              <>
                <div className="space-y-1">
                  <p className="font-medium">上线国家 (contentLangs)</p>
                  {contentLangs.length === 0 ? (
                    <p className="text-muted-foreground">未上线 / 无</p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {contentLangs.map((code) => (
                        <Badge key={code} variant="outline">
                          {formatContentLang(code)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="font-medium">上线产品 (productIds)</p>
                  {productIds.length === 0 ? (
                    <p className="text-muted-foreground">未上任何产品</p>
                  ) : (
                    <div className="flex flex-wrap gap-1">
                      {productIds.map((code) => (
                        <Badge key={code} variant="outline">
                          {formatProductId(code)}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const Actions = ({ avatar }) => {
  const [open, setOpen] = useState(false);
  const { can } = usePermissions();
  const canDeleteAvatar = can("deleteAvatar");

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="w-full">
          Actions
          <ChevronDownIcon className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-56"
        onInteractOutside={() => setOpen(false)} // 点击外部时关闭菜单
      >
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <ActionSetSimple avatar={avatar} />
          <ActionSetCover avatar={avatar} />
          <ActionSetPreview avatar={avatar} />
          <ActionSetDlcCover avatar={avatar} />
          <ActionSetTag avatar={avatar} />
          <AvatarActionCopyId avatar={avatar} />
          <AvatarActionCopyShortId avatar={avatar} />
          <AvatarActionCopyTid avatar={avatar} />
          <AvatarActionCopyImageUrl avatar={avatar} />
          <AvatarActionCopyImageThumbUrl avatar={avatar} />
          <AvatarGotoEditor avatar={avatar} />
          <AvatarGotoView avatar={avatar} />
          <ActionCheckFrameStatus avatar={avatar} />
        </DropdownMenuGroup>

        <DropdownMenuLabel className="text-destructive mt-2">
          Please pay attention
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <ActionSetOffline avatar={avatar} />
          <ActionSetDefaultCarousel avatar={avatar} />
          {canDeleteAvatar ? <AvatarActionDelete avatar={avatar} /> : null}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
